import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { TargetTypeEnum } from '@ais-ui/websockets';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { PiAnchor, PiBoat, PiX } from 'react-icons/pi';
import { useAISMapContext } from '../AISMapContext';
import { useLocalization } from '@ais-ui/locale-management';
import { SearchExtendedVesselInfo } from './SearchExtendedVesselInfo';
import { AISShipSearch } from './AISShipSearch';
import { ShipSearchResult } from './useShipSearch';
import { useFetchShipByMmsi } from '@ais-ui/api';
import { PortSearch } from './PortSearch';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { usePermissions } from '../../Permissions/PermissionsProvider';

interface ShipSearchInputProps {
  className?: string;
  setSearchString: React.Dispatch<React.SetStateAction<string>>;
  searchString: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  inputRef: React.RefObject<HTMLInputElement>;
}

export const ShipSearchInput: React.FC<ShipSearchInputProps> = ({
  className,
  setSearchString,
  searchString,
  setOpen,
  inputRef,
}) => {
  const { messages } = useLocalization();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    if (searchString.length > 0) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleOnFocus = () => {
    setOpen(true);
  };

  const handleOnBlur = () => {
    if (searchString.length === 0) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (searchString.length > 0) {
      setOpen(true);
    }
  }, [searchString, setOpen]);

  const onClearSearch = () => {
    setSearchString('');
    setOpen(false);
  };

  return (
    <PopoverTrigger asChild>
      <div className={cn('relative w-80', className)}>
        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          className="pl-10 border-none focus-visible:ring-0"
          placeholder={messages.shipSearchText}
          value={searchString}
          onChange={handleSearchChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          autoFocus
        />
        {searchString.length > 0 && (
          <Tooltip>
            <TooltipTrigger
              className="absolute inset-y-0 right-0 flex items-center"
              asChild
            >
              <Button
                variant="ghost"
                size="icon"
                className="absolute inset-y-0 right-8 top-2 flex items-center h-6 w-6"
                onClick={onClearSearch}
              >
                <PiX />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {messages.shipSearchClearSearchTooltip}
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <IoMdInformationCircleOutline className="text-primary" />
          </TooltipTrigger>
          <TooltipContent side="right">
            {messages.shipSearchTooltip}
          </TooltipContent>
        </Tooltip>
      </div>
    </PopoverTrigger>
  );
};

interface ShipPortSearchInputProps {
  className?: string;
  setSearchString: React.Dispatch<React.SetStateAction<string>>;
  searchString: string;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  inputRef: React.RefObject<HTMLInputElement>;
  searchType: string;
  setSearchType: React.Dispatch<React.SetStateAction<'ship' | 'port'>>;
}

export const ShipPortSearchInput: React.FC<ShipPortSearchInputProps> = ({
  className,
  setSearchString,
  searchString,
  setIsFocused,
  inputRef,
  searchType,
  setSearchType,
}) => {
  const { messages } = useLocalization();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchString(newValue);
  };
  const handleOnFocus = () => {
    setIsFocused(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onClearSearch = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering popover toggle
    setSearchString('');
    setIsFocused(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onChangeSearchType = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent triggering popover toggle

      setIsFocused(true);

      setSearchType((prevType) => (prevType === 'ship' ? 'port' : 'ship'));
      setSearchString('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    [setIsFocused, setSearchType, setSearchString, inputRef]
  );

  const handleContainerClick = () => {
    setIsFocused(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <PopoverTrigger className={'popover-trigger'} asChild>
      <div
        className={cn('relative w-80', className)}
        onClick={handleContainerClick}
      >
        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          className="pl-10 border-none focus-visible:ring-0"
          placeholder={
            searchType === 'ship'
              ? messages.shipSearchText
              : messages.portSearchText
          }
          value={searchString}
          onChange={handleSearchChange}
          onFocus={handleOnFocus}
        />
        {searchString.length > 0 && (
          <Tooltip>
            <TooltipTrigger
              className="absolute inset-y-0 right-0 flex items-center"
              asChild
            >
              <Button
                variant="ghost"
                size="icon"
                className="absolute inset-y-0 right-14 top-2 flex items-center h-6 w-6"
                onClick={onClearSearch}
              >
                <PiX />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {messages.mapShipPortSearchClearSearchTooltip}
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger
            className="absolute inset-y-0 right-0 flex items-center"
            asChild
          >
            <Button
              id={
                searchType === 'ship'
                  ? 'change-to-port-search-button'
                  : 'change-to-ship-search-button'
              }
              variant="ghost"
              size="icon"
              className="absolute inset-y-0 right-8 top-2 flex items-center h-6 w-6"
              onClick={onChangeSearchType}
            >
              {searchType === 'ship' ? <PiBoat /> : <PiAnchor />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {searchType === 'ship'
              ? 'Change to port search'
              : 'Change to ship search'}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <IoMdInformationCircleOutline className="text-primary" />
          </TooltipTrigger>
          <TooltipContent side="right">
            {searchType === 'ship'
              ? messages.shipSearchTooltip
              : messages.portSearchTooltip}
          </TooltipContent>
        </Tooltip>
      </div>
    </PopoverTrigger>
  );
};

interface ShipSearchProps {
  className?: string;
}

export const ShipPortSearch: React.FC<ShipSearchProps> = React.memo(
  ({ className }) => {
    const [searchString, setSearchString] = useState('');
    const [searchType, setSearchType] = useState<'ship' | 'port'>('ship');
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const { messages } = useLocalization();
    const permissions = usePermissions();

    const hasShipDbPermissions =
      permissions.mdmAdvancedVesselInfo ||
      permissions.mdmRegister ||
      permissions.mdmIntegrity;

    const { setTargetInfoDetailsList } = useSelectionState();
    const { setZoomToShip } = useAISMapContext();
    const { toast } = useToast();

    const fetchShipByMmsi = useFetchShipByMmsi();

    const onSearchShipDbByMmsi = useCallback(
      async (mmsi: number) => {
        try {
          const ship = await fetchShipByMmsi(mmsi);
          if (!ship) {
            toast({
              className: 'extended-vessel-info',
              title: 'Not Found!',
              description: messages.mapShipPortSearchNotFoundInEviDescription,
              variant: 'destructive',
            });
            setIsFocused(false);
            return;
          }

          setTargetInfoDetailsList([
            {
              extractedTargetInfo: [ship],
              extractedTargetInfoType: TargetTypeEnum.ExtendedVessel,
            },
          ]);
          setIsFocused(false);
        } catch (error) {
          toast({
            title: 'Error!',
            description: messages.mapShipPortSearchNotFoundInEviError,
            variant: 'destructive',
          });
          setIsFocused(false);
        }
      },
      [fetchShipByMmsi, setTargetInfoDetailsList, toast]
    );

    const onAISShipClick = useCallback(
      (ship: ShipSearchResult) => {
        if (ship.name) {
          setSearchString(ship.name);
        }

        if (ship.requestType === 'ais') {
          setZoomToShip(ship);
          setTimeout(() => {
            setIsFocused(false);
          }, 300);
        } else if (ship.requestType === 'no-recent-ais') {
          setZoomToShip(ship);
          toast({
            title: messages.genericToastTitleWarning,
            description: messages.mapShipPortSearchNoRecentAisFoundWarning,
            variant: 'warning',
          });
          setTimeout(() => {
            setIsFocused(false);
          }, 300);
        } else {
          if (hasShipDbPermissions && ship.mmsi) {
            onSearchShipDbByMmsi(Number(ship.mmsi));
          } else {
            let description;
            if (!hasShipDbPermissions && ship.mmsi) {
              description = messages.mapShipPortSearchMmsiPresentNoShipDB;
            } else if (!hasShipDbPermissions && !ship.mmsi) {
              description = messages.mapShipPortSearchNoPermissionsWarning;
            } else {
              // hasShipDbPermissions && !ship.mmsi
              description = messages.mapShipPortSearchNoMmsiFoundWarning;
            }

            toast({
              title: messages.genericToastTitleWarning,
              description,
              variant: 'warning',
            });
          }
        }
      },
      [
        hasShipDbPermissions,
        onSearchShipDbByMmsi,
        setZoomToShip,
        toast,
        messages,
      ]
    );

    useEffect(() => {
      if (inputRef.current && isFocused) {
        // Add a small timeout to ensure the Popover has rendered
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 50);
      }
    }, [isFocused]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const popoverContent = document.querySelector('.popover-content');
        const popoverTrigger = document.querySelector('.popover-trigger');
        if (
          popoverTrigger &&
          !popoverTrigger.contains(event.target as Node) &&
          popoverContent &&
          !popoverContent.contains(event.target as Node)
        ) {
          setIsFocused(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    return (
      <Popover open={isFocused}>
        <ShipPortSearchInput
          className={className}
          searchString={searchString}
          setSearchString={setSearchString}
          setIsFocused={setIsFocused}
          inputRef={inputRef}
          searchType={searchType}
          setSearchType={setSearchType}
        />
        <PopoverContent autoFocus={false} className="w-80 popover-content">
          {searchType === 'ship' && (
            <SearchExtendedVesselInfo
              searchString={searchString}
              setIsFocused={setIsFocused}
            />
          )}
          {searchType === 'ship' && <Separator />}
          {searchType === 'ship' && (
            <AISShipSearch
              searchString={searchString}
              onClick={onAISShipClick}
            />
          )}
          {searchType === 'port' && (
            <PortSearch
              searchString={searchString}
              setSearchString={setSearchString}
            />
          )}
        </PopoverContent>
      </Popover>
    );
  }
);

ShipPortSearch.displayName = 'ShipPortSearch';
